import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import ShiftStatusCard from "@/components/ShiftStatusCard";
import { MyLeave } from "@/components/LeavePanel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, MapPin, Coffee, Repeat, Megaphone, UserPlus, AlertTriangle, Inbox } from "lucide-react";

const STATUS = {
  awaiting_colleague: { label: "Awaiting colleague", cls: "bg-amber-50 text-amber-600" },
  awaiting_manager: { label: "Awaiting manager approval", cls: "bg-sky-50 text-sky-600" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-600" },
  approved_awaiting_republish: { label: "Approved — awaiting rota republish", cls: "bg-emerald-50 text-emerald-600" },
  declined: { label: "Declined", cls: "bg-slate-100 text-slate-500" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-400" },
};
function statusFor(r) {
  if (r.status === "awaiting_manager" && r.volunteer_person_id) return { label: "Colleague accepted · awaiting manager approval", cls: "bg-sky-50 text-sky-600" };
  return STATUS[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-500" };
}
const kindLabel = (r) => (r.kind === "swap" ? "Swap" : r.cover_mode === "nominated" ? "Cover (asked colleague)" : "Cover (offer to team)");

export default function MyRota() {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [opps, setOpps] = useState([]);
  const [modal, setModal] = useState(null); // {type:'swap'|'ask', shift}
  const [openShifts, setOpenShifts] = useState([]);

  const load = useCallback(() => {
    api.get("/workforce/my-rota").then((r) => setData(r.data)).catch(() => setData({ linked: false, shifts: [] }));
    api.get("/workforce/open-shifts").then((r) => setOpenShifts(r.data || [])).catch(() => setOpenShifts([]));
    api.get("/workforce/change-requests?box=mine").then((r) => setMine(r.data)).catch(() => setMine([]));
    api.get("/workforce/change-requests?box=opportunities").then((r) => setOpps(r.data)).catch(() => setOpps([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const confirmShift = async (s) => {
    try { await api.post(`/workforce/shifts/${s.shift_id}/confirm`); toast.success("Shift confirmed"); load(); }
    catch (e) { toast.error(e?.normalized?.message || "Could not confirm this shift"); }
  };
  const requestOpen = async (o) => {
    try { await api.post(`/workforce/open-shifts/${o.open_shift_id}/request`); toast.success("Request sent — a manager will allocate this shift"); load(); }
    catch (e) { toast.error(e?.normalized?.message || "Could not request this shift"); }
  };

  const offerToTeam = async (shift) => {
    try { await api.post("/workforce/change-requests", { kind: "cover", shift_id: shift.shift_id, cover_mode: "open" }); toast.success("Offered to the team"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not offer shift"); }
  };
  const cancel = async (id) => { try { await api.post(`/workforce/change-requests/${id}/cancel`); toast.success("Request cancelled"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const volunteer = async (id) => { try { await api.post(`/workforce/change-requests/${id}/volunteer`); toast.success("You volunteered — awaiting manager approval"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const accept = async (id) => { try { await api.post(`/workforce/change-requests/${id}/accept`); toast.success("Accepted — awaiting manager approval"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const declineColleague = async (id) => { try { await api.post(`/workforce/change-requests/${id}/decline-colleague`); toast.success("Declined"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (!data) return <div className="p-6 text-slate-400" data-testid="myrota-loading">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="myrota-page">
      <h1 className="text-2xl font-bold text-slate-800">My Rota</h1>

      <ShiftStatusCard />

      {!data.linked ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-slate-500" data-testid="myrota-unlinked">
          Your account isn't linked to a workforce profile yet. Ask your manager to link it so your shifts appear here.
        </div>
      ) : (
        <>
          {openShifts.length > 0 && (
            <div className="bg-white border rounded-2xl p-4" data-testid="myrota-open-shifts">
              <h3 className="font-semibold text-slate-800 mb-2">Open shifts you can request</h3>
              {openShifts.map((o) => (
                <div key={o.open_shift_id} className="flex items-center justify-between py-2 border-t first:border-t-0" data-testid={`myrota-open-${o.open_shift_id}`}>
                  <div className="text-sm text-slate-700">{o.date} · {o.start} – {o.end} {o.department ? `· ${o.department}` : ""} · {o.venue_name}</div>
                  {o.requested
                    ? <span className="text-xs text-slate-500">Requested</span>
                    : <Button size="sm" onClick={() => requestOpen(o)} data-testid={`myrota-open-request-${o.open_shift_id}`}>Request</Button>}
                </div>
              ))}
            </div>
          )}
          {/* Opportunities */}
          {opps.length > 0 && (
            <section data-testid="myrota-opportunities">
              <h2 className="font-semibold text-slate-700 mb-2 flex items-center gap-2"><Inbox size={16} className="text-brand" /> Shifts you could pick up</h2>
              <div className="space-y-2">
                {opps.map((r) => {
                  const s = r.shift_public || {};
                  const nominated = r.cover_mode === "nominated";
                  return (
                    <div key={r.request_id} className="bg-white border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`opp-${r.request_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800">{s.date} · {s.start}–{s.end}</div>
                        <div className="text-xs text-slate-500">{s.site_name} · from {r.requester_person_name}{nominated ? " · asked you directly" : ""}{r.note ? ` · “${r.note}”` : ""}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {nominated ? (
                          <>
                            <Button size="sm" className="flex-1 sm:flex-none min-h-[40px] bg-brand hover:bg-brand-hover" data-testid={`opp-accept-${r.request_id}`} onClick={() => accept(r.request_id)}>Accept</Button>
                            <Button size="sm" variant="outline" className="flex-1 sm:flex-none min-h-[40px]" data-testid={`opp-decline-${r.request_id}`} onClick={() => declineColleague(r.request_id)}>Decline</Button>
                          </>
                        ) : (
                          <Button size="sm" className="flex-1 sm:flex-none min-h-[40px] bg-brand hover:bg-brand-hover" data-testid={`opp-volunteer-${r.request_id}`} onClick={() => volunteer(r.request_id)}>Volunteer</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Swap requests addressed to me (as target) */}
          {mine.filter((r) => r.kind === "swap" && r.status === "awaiting_colleague" && r.target_person_id && r.requester_person_id !== (data.person?.person_id)).length > 0 && (
            <section data-testid="myrota-swap-inbox">
              <h2 className="font-semibold text-slate-700 mb-2 flex items-center gap-2"><Repeat size={16} className="text-brand" /> Swap requests for you</h2>
              <div className="space-y-2">
                {mine.filter((r) => r.kind === "swap" && r.status === "awaiting_colleague" && r.target_person_id === data.person?.person_id).map((r) => (
                  <div key={r.request_id} className="bg-white border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`swapin-${r.request_id}`}>
                    <div className="text-sm min-w-0">
                      <div className="font-medium text-slate-800">{r.requester_person_name} wants to swap</div>
                      <div className="text-xs text-slate-500">You'd work {r.shift_public?.date} {r.shift_public?.start}–{r.shift_public?.end}; they'd take yours {r.target_shift_public?.date} {r.target_shift_public?.start}–{r.target_shift_public?.end}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" className="flex-1 sm:flex-none min-h-[40px] bg-brand hover:bg-brand-hover" data-testid={`swapin-accept-${r.request_id}`} onClick={() => accept(r.request_id)}>Accept</Button>
                      <Button size="sm" variant="outline" className="flex-1 sm:flex-none min-h-[40px]" data-testid={`swapin-decline-${r.request_id}`} onClick={() => declineColleague(r.request_id)}>Decline</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Upcoming shifts */}
          <section>
            <h2 className="font-semibold text-slate-700 mb-2">Upcoming shifts</h2>
            {data.shifts.length === 0 ? (
              <div className="bg-white border rounded-2xl p-8 text-center text-slate-500" data-testid="myrota-empty">No upcoming published shifts.</div>
            ) : (
              <div className="space-y-3">
                {data.shifts.map((s) => {
                  const open = mine.find((r) => r.shift_id === s.shift_id && (r.status === "awaiting_colleague" || r.status === "awaiting_manager"));
                  return (
                    <div key={s.shift_id} className="bg-white border rounded-2xl p-4" data-testid={`myrota-shift-${s.shift_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-slate-800 flex items-center gap-2"><CalendarClock size={16} className="text-brand" /> {s.date}</div>
                          <div className="text-sm text-slate-600 mt-1">{s.start} – {s.end} · {s.hours}h {s.role ? `· ${s.role}` : ""}</div>
                          {s.notes ? <div className="text-xs text-slate-500 mt-0.5" data-testid={`myrota-notes-${s.shift_id}`}>{s.notes}</div> : null}
                          {s.confirmation?.status === "confirmed"
                            ? <div className="text-xs text-emerald-700 mt-1" data-testid={`myrota-confirmed-${s.shift_id}`}>Confirmed</div>
                            : <Button size="sm" variant="outline" className="mt-2" onClick={() => confirmShift(s)} data-testid={`myrota-confirm-${s.shift_id}`}>
                                {s.confirmation?.status === "reconfirm_required" ? "Shift changed — confirm again" : "Confirm shift"}
                              </Button>}
                          <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                            <span className="flex items-center gap-1"><MapPin size={12} /> {s.venue_name || "Site"}</span>
                            {s.unpaid_break_minutes ? <span className="flex items-center gap-1"><Coffee size={12} /> {s.unpaid_break_minutes}m break</span> : null}
                          </div>
                        </div>
                        <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-lg bg-emerald-50 text-emerald-600">{s.department}</span>
                      </div>
                      {open ? (
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className={`px-2 py-1 rounded-lg font-medium ${statusFor(open).cls}`} data-testid={`shift-req-status-${s.shift_id}`}>{kindLabel(open)} · {statusFor(open).label}</span>
                          <button className="text-slate-400 hover:text-red-500" data-testid={`shift-req-cancel-${s.shift_id}`} onClick={() => cancel(open.request_id)}>Cancel request</button>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" data-testid={`req-swap-${s.shift_id}`} onClick={() => setModal({ type: "swap", shift: s })}><Repeat size={13} className="mr-1" /> Request swap</Button>
                          <Button size="sm" variant="outline" data-testid={`offer-team-${s.shift_id}`} onClick={() => offerToTeam(s)}><Megaphone size={13} className="mr-1" /> Offer to team</Button>
                          <Button size="sm" variant="outline" data-testid={`ask-colleague-${s.shift_id}`} onClick={() => setModal({ type: "ask", shift: s })}><UserPlus size={13} className="mr-1" /> Ask a colleague</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* My requests history */}
          {mine.length > 0 && (
            <section data-testid="myrota-requests">
              <h2 className="font-semibold text-slate-700 mb-2">My requests</h2>
              <div className="bg-white border rounded-2xl divide-y">
                {mine.map((r) => (
                  <div key={r.request_id} className="px-4 py-3 flex items-center justify-between" data-testid={`myreq-${r.request_id}`}>
                    <div className="text-sm">
                      <div className="font-medium text-slate-800">{kindLabel(r)} · {r.shift_public?.date} {r.shift_public?.start}–{r.shift_public?.end}</div>
                      <div className="text-xs text-slate-500">{r.requester_person_id === data.person?.person_id ? "You offered" : `From ${r.requester_person_name}`}{r.volunteer_person_name ? ` · ${r.volunteer_person_name} covering` : ""}{r.target_person_name ? ` · with ${r.target_person_name}` : ""}</div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${statusFor(r).cls}`}>{statusFor(r).label}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          <MyLeave />
        </>
      )}

      {modal && <PickerModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

function PickerModal({ modal, onClose, onDone }) {
  const { type, shift } = modal;
  const [options, setOptions] = useState(null);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    const url = type === "swap" ? `/workforce/change-requests/swap-candidates?shift_id=${shift.shift_id}` : `/workforce/change-requests/colleagues?shift_id=${shift.shift_id}`;
    api.get(url).then((r) => setOptions(r.data)).catch(() => setOptions([]));
  }, [type, shift]);
  const submit = async () => {
    if (!sel) return toast.error(type === "swap" ? "Choose a shift to swap with" : "Choose a colleague");
    try {
      const body = type === "swap"
        ? { kind: "swap", shift_id: shift.shift_id, target_shift_id: sel, note }
        : { kind: "cover", shift_id: shift.shift_id, cover_mode: "nominated", nominated_person_id: sel, note };
      await api.post("/workforce/change-requests", body);
      toast.success(type === "swap" ? "Swap requested" : "Colleague asked");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="picker-modal">
        <DialogHeader><DialogTitle>{type === "swap" ? "Request a swap" : "Ask a colleague to cover"}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 mb-2">Your shift: {shift.date} · {shift.start}–{shift.end}</div>
        {options === null ? <div className="text-slate-400 text-sm py-4">Loading…</div>
          : options.length === 0 ? <div className="text-slate-400 text-sm py-4" data-testid="picker-empty">{type === "swap" ? "No colleague shifts available to swap with this week." : "No eligible colleagues available."}</div>
          : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {options.map((o) => {
                const id = type === "swap" ? o.shift_id : o.person_id;
                return (
                  <button key={id} onClick={() => setSel(id)} data-testid={`picker-opt-${id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${sel === id ? "border-brand bg-brand-light" : "border-slate-200"}`}>
                    {type === "swap"
                      ? <span><span className="font-medium">{o.person_name}</span> · {o.date} {o.start}–{o.end}</span>
                      : <span><span className="font-medium">{o.display_name}</span>{o.job_title ? ` · ${o.job_title}` : ""}{(o.warnings || []).length ? <AlertTriangle size={12} className="inline ml-1 text-amber-500" /> : null}</span>}
                  </button>
                );
              })}
            </div>
          )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={submit} data-testid="picker-submit" disabled={!options || options.length === 0}>Send request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
